/**
 * @author        esraa.afifi@zumper.com (Esraa Afifi)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   MapBox Module Data Reducer
 */

import { payloadReducer } from '@zcode/reducers'
import type { MainMapDataTypes } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import { combineReducers } from 'redux'
import { type ExtractActions, type Values, handleActions } from 'redux-actions'
import type * as actions from '../actions'
import {
  RESET_CENTER,
  UPDATE_BEARING,
  UPDATE_CENTER,
  UPDATE_DIMENSIONS,
  UPDATE_HEIGHT,
  UPDATE_LATITUDE,
  UPDATE_LONGITUDE,
  UPDATE_PITCH,
  UPDATE_WIDTH,
  UPDATE_ZOOM,
} from '../constants'
import metaReducer from './meta'

type Actions = ExtractActions<Values<typeof actions>>

const payloadExceptUndefinedReducer = <State, Payload>(
  state: State,
  { payload }: { payload: Payload | undefined }
) => {
  if (typeof payload !== 'undefined') {
    return payload
  }
  return state
}

const dataReducer = combineReducers<MainMapDataTypes, Actions>({
  latitude: handleActions(
    {
      [UPDATE_LATITUDE]: payloadExceptUndefinedReducer,
      [UPDATE_CENTER]: (state, action) => {
        const { payload } = action
        if (payload && isNumber(payload.lat)) {
          return payload.lat
        }

        return state
      },
      [RESET_CENTER]: () => null,
    },
    null as MainMapDataTypes['latitude']
  ),
  longitude: handleActions(
    {
      [UPDATE_LONGITUDE]: payloadExceptUndefinedReducer,
      [UPDATE_CENTER]: (state, action) => {
        const { payload } = action

        if (payload && isNumber(payload.lng)) {
          return payload.lng
        }

        return state
      },
      [RESET_CENTER]: () => null,
    },
    null as MainMapDataTypes['longitude']
  ),
  zoom: handleActions(
    {
      [UPDATE_ZOOM]: (state, action) => {
        const { payload } = action
        if (payload && isNumber(payload)) {
          return payload
        }

        return state
      },
    },
    8 // schema had default of null
  ),
  bearing: handleActions(
    {
      [UPDATE_BEARING]: (state, action) => {
        const { payload } = action

        if (payload && isNumber(payload)) {
          return payload
        }

        return state
      },
    },
    0
  ),
  pitch: handleActions(
    {
      [UPDATE_PITCH]: (state, action) => {
        const { payload } = action

        if (payload && isNumber(payload)) {
          return payload
        }

        return state
      },
    },
    0
  ),
  width: handleActions(
    {
      [UPDATE_WIDTH]: payloadReducer,
      [UPDATE_DIMENSIONS]: (state, action) => {
        const { payload } = action

        if (payload && isNumber(payload.width)) {
          return payload.width
        }

        return state
      },
    },
    null as MainMapDataTypes['width'] // schema had default of 0
  ),
  height: handleActions(
    {
      [UPDATE_HEIGHT]: payloadReducer,
      [UPDATE_DIMENSIONS]: (state, action) => {
        const { payload } = action

        if (payload && isNumber(payload.height)) {
          return payload.height
        }

        return state
      },
    },
    null as MainMapDataTypes['height'] // schema had default of 0
  ),
})

export const mapReducer = combineReducers({
  data: dataReducer,
  meta: metaReducer,
})
